// Абстракция над хранилищем состояния приложения.
// Никогда не бросает исключений наружу — любая ошибка глушится внутри.

export interface StorageAdapter {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  readonly kind: 'localStorage' | 'memory';
}

const PROBE_KEY = '__plan_flights_storage_probe__';

/** In-memory реализация — используется по умолчанию, если localStorage недоступен. */
export function memoryStorage(): StorageAdapter {
  const data = new Map<string, string>();
  return {
    kind: 'memory',
    get(key: string): string | null {
      try {
        return data.has(key) ? (data.get(key) ?? null) : null;
      } catch {
        return null;
      }
    },
    set(key: string, value: string): void {
      try {
        data.set(key, value);
      } catch {
        // глушим — in-memory реализация не должна бросать наружу
      }
    },
    remove(key: string): void {
      try {
        data.delete(key);
      } catch {
        // глушим
      }
    },
  };
}

function isLocalStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const storage = window.localStorage;
    storage.setItem(PROBE_KEY, '1');
    const readBack = storage.getItem(PROBE_KEY);
    storage.removeItem(PROBE_KEY);
    return readBack === '1';
  } catch {
    return false;
  }
}

function localStorageAdapter(): StorageAdapter {
  return {
    kind: 'localStorage',
    get(key: string): string | null {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key: string, value: string): void {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // например QuotaExceededError — глушим
      }
    },
    remove(key: string): void {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // глушим
      }
    },
  };
}

/** Feature-detect localStorage, при любой проблеме — in-memory fallback. */
export function createStorage(): StorageAdapter {
  if (isLocalStorageAvailable()) {
    return localStorageAdapter();
  }
  return memoryStorage();
}
